import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Save, Trash2, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });

export function CoAdminManager({ token }) {
  const [co, setCo] = useState(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const load = async () => {
    try {
      const d = await axios.get(`${API}/admin/coadmin`, auth(token)).then((r) => r.data);
      if (d && d.username) { setCo(d); setUsername(d.username); } else { setCo(null); }
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const err = (e) => toast.error(e.response?.data?.detail || "Something went wrong");

  const saveAccount = async () => {
    if (!username.trim()) return toast.error("Enter a username");
    if (!co && !password.trim()) return toast.error("Set a password to create the co-admin");
    try {
      await axios.post(`${API}/admin/coadmin`, { username: username.trim(), password: password.trim() }, auth(token));
      setPassword("");
      toast.success(co ? "Co-admin updated" : "Co-admin created");
      load();
    } catch (e) { err(e); }
  };
  const toggle = async () => { try { const d = await axios.post(`${API}/admin/coadmin/toggle`, {}, auth(token)).then(r=>r.data); toast.success(d.enabled ? "Login shown" : "Login hidden — active sessions logged out"); load(); } catch (e) { err(e); } };
  const stockToggle = async () => { try { const d = await axios.post(`${API}/admin/coadmin/stock-toggle`, {}, auth(token)).then(r=>r.data); toast.success(`View Stock ${d.show_stock ? "enabled" : "hidden"} for co-admin`); load(); } catch (e) { err(e); } };
  const del = async () => { if (!window.confirm("Delete the co-admin account? They will be logged out immediately.")) return; try { await axios.delete(`${API}/admin/coadmin`, auth(token)); toast.success("Co-admin deleted"); setCo(null); setUsername(""); } catch (e) { err(e); } };

  return (
    <div className="max-w-xl space-y-4" data-testid="coadmin-manager">
      <h2 className="font-display font-black text-2xl tracking-tight text-white">Co-Admin Account</h2>
      <p className="text-xs text-white/50">Only you can set or change the co-admin's password. Hiding the login logs the co-admin out immediately and removes it from the login screen. Orders they already placed stay in your Orders panel.</p>

      <div className="border border-white/10 bg-[#0A0A0A] p-4 space-y-3">
        <div className="flex items-center gap-2">
          <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border ${co ? (co.enabled ? "border-emerald-500/40 text-emerald-400" : "border-orange-500/40 text-orange-400") : "border-white/20 text-white/40"}`} data-testid="coadmin-status">
            {co ? (co.enabled ? "Active / Visible" : "Hidden") : "Not created"}
          </span>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-white/40 block mb-1">Username</Label>
          <Input value={username} onChange={(e) => setUsername(e.target.value)} data-testid="coadmin-username" className="rounded-none h-10 bg-[#141414] border-white/10 text-white text-sm" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-white/40 block mb-1">{co ? "New password (leave blank to keep)" : "Password"}</Label>
          <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={co ? "••••••" : "Set a password"} data-testid="coadmin-password" className="rounded-none h-10 bg-[#141414] border-white/10 text-white text-sm" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={saveAccount} data-testid="coadmin-save" className="rounded-none h-9 bg-white text-black hover:bg-white/90 text-xs uppercase tracking-wider"><Save className="h-3.5 w-3.5 mr-1" /> {co ? "Save" : "Create"}</Button>
          {co && <Button onClick={toggle} data-testid="coadmin-toggle" variant="outline" className="rounded-none h-9 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider">{co.enabled ? <><EyeOff className="h-3.5 w-3.5 mr-1" /> Hide login</> : <><Eye className="h-3.5 w-3.5 mr-1" /> Unhide login</>}</Button>}
          {co && <Button onClick={stockToggle} data-testid="coadmin-stock-toggle" variant="outline" className="rounded-none h-9 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider"><Package className="h-3.5 w-3.5 mr-1" /> {co.show_stock ? "Hide Stock" : "Unhide Stock"}</Button>}
          {co && <Button onClick={del} data-testid="coadmin-delete" variant="outline" className="rounded-none h-9 border-red-500/40 bg-transparent text-red-400 hover:bg-red-500 hover:text-white text-xs uppercase tracking-wider"><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>}
        </div>
      </div>
    </div>
  );
}
